import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, Check, ChevronDown, Inbox, LayoutGrid, List, Loader2, Search,
} from 'lucide-react';
import { tx } from './i18n';

// The shared vocabulary every XOmics screen is built from, so a table on the
// staff page and a table on the analytics page are the same object rather than
// two people's idea of one.
//
// Every class is scoped under #xo — Create React App puts all sixty of
// szaportal's stylesheets into one global sheet, several of which already
// define .card, .panel, .stat and .pill.

/** Counts to a new value, because a number that changes should be seen to. */
export function Num({ value }) {
  const [display, setDisplay] = useState(value);
  const previous = useRef(null);
  const frame = useRef();

  useEffect(() => {
    const target = Number(value);
    if (!Number.isFinite(target)) { setDisplay(value); return undefined; }
    const from = previous.current;
    if (from === null || from === target
      || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(String(target)); previous.current = target; return undefined;
    }
    const started = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - started) / 320);
      setDisplay(String(Math.round(from + (target - from) * (1 - (1 - progress) ** 3))));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
      else previous.current = target;
    };
    frame.current = requestAnimationFrame(tick);
    return () => { if (frame.current) cancelAnimationFrame(frame.current); };
  }, [value]);

  return <>{display}</>;
}

/**
 * One measurement. `tone` colours the icon square only — the label always says
 * the same thing in words, so nothing depends on colour vision.
 */
export function Metric({
  icon: Icon, label, value, unit, note, noteTone, tone = 'flat', live, aside,
  as: Tag = 'div', ...rest
}) {
  return (
    <Tag className="xo-metric" {...rest}>
      {Icon && <span className={`xo-metric-icon t-${tone}`} aria-hidden="true"><Icon /></span>}
      <span className="xo-metric-main">
        <span className="xo-metric-label">{label}</span>
        <span className="xo-metric-value">
          <Num value={value} />{unit && <small> {unit}</small>}
        </span>
        {note && <span className={`xo-metric-note${noteTone ? ` t-${noteTone}` : ''}`}>{note}</span>}
      </span>
      {(live || aside) && (
        <span className="xo-metric-aside">
          {live && <span className="xo-live">{tx("ui.parts.live_65c821a5")}</span>}
          {aside}
        </span>
      )}
    </Tag>
  );
}

export const Metrics = ({ children }) => <div className="xo-metrics">{children}</div>;

// A quiet alternative to KPI cards for catalogue and infrastructure screens.
// The values stay easy to scan, but they do not compete with the rows below.
export const SummaryBar = ({ children }) => <div className="xo-summary-bar">{children}</div>;

export function SummaryItem({ value, label, detail, tone = 'flat' }) {
  return (
    <div className={`xo-summary-item t-${tone}`}>
      <span><strong>{value}</strong><small>{label}</small></span>
      {detail && <p>{detail}</p>}
    </div>
  );
}

export function ViewSwitch({ value, onChange, label = tx('ui.parts.layout') }) {
  return (
    <div className="xo-view-switch" role="group" aria-label={label}>
      <button type="button" className={value === 'grid' ? 'is-active' : ''}
        aria-pressed={value === 'grid'} onClick={() => onChange('grid')}>
        <LayoutGrid aria-hidden="true" /><span>{tx("ui.parts.grid_701c483f")}</span>
      </button>
      <button type="button" className={value === 'list' ? 'is-active' : ''}
        aria-pressed={value === 'list'} onClick={() => onChange('list')}>
        <List aria-hidden="true" /><span>{tx("ui.parts.list_a1fffaaa")}</span>
      </button>
    </div>
  );
}

/**
 * The primary switch used across XOmics workspaces. Tabs share one visual and
 * interaction model whether they sit above a catalogue or inside a side rail.
 */
export function WorkspaceTabs({
  value, tabs, onChange, label, className = '', idPrefix, panelId, scrollable = false,
}) {
  const classes = [
    'xo-workspace-tabs', className, scrollable ? 'is-scrollable' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={classes} role="tablist" aria-label={label}
      style={{ '--xo-workspace-tab-count': tabs.length }}>
      {tabs.map((tab) => {
        const Icon = tab.Icon;
        const count = typeof tab.count === 'number' ? tab.count.toLocaleString() : tab.count;
        return (
          <button type="button" role="tab" key={tab.value}
            id={idPrefix ? `${idPrefix}-${tab.value}` : undefined}
            aria-controls={panelId}
            aria-selected={value === tab.value}
            className={value === tab.value ? 'is-active' : ''}
            onClick={() => onChange(tab.value)}>
            {Icon && <Icon aria-hidden="true" />}
            <span>{tab.label}</span>
            {count !== undefined && count !== null && <b>{count}</b>}
          </button>
        );
      })}
    </div>
  );
}

/** A consistent title for the activity rails beside map and workflow pages. */
export function RailHeader({ title, subtitle }) {
  return (
    <header className="xo-rail-header">
      <strong>{title}</strong>
      {subtitle && <span>{subtitle}</span>}
    </header>
  );
}

/** The single search-field shape used by lists, rails, and catalogues. */
export function SearchField({
  value, onChange, placeholder, label = placeholder, className = '', children, ...inputProps
}) {
  return (
    <label className={`xo-search-field${className ? ` ${className}` : ''}`}>
      <Search aria-hidden="true" />
      <input {...inputProps} type="search" value={value} placeholder={placeholder} aria-label={label}
        onChange={(event) => onChange(event.target.value)} />
      {children}
    </label>
  );
}

// Leaving a form that opened over a page: not while it is saving, and with
// something typed only after being asked. Escape leaves the same way.
export function useLeave(busy, touched, onClose) {
  const leave = useCallback(() => {
    if (busy) return;
    if (touched && !window.confirm(tx('ui.products.detail.discard_changes'))) return;
    onClose();
  }, [busy, onClose, touched]);
  useEffect(() => {
    const leaveOnEscape = (event) => { if (event.key === 'Escape') leave(); };
    window.addEventListener('keydown', leaveOnEscape);
    return () => window.removeEventListener('keydown', leaveOnEscape);
  }, [leave]);
  return leave;
}

function optionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(optionText).join('');
  if (React.isValidElement(node)) return optionText(node.props.children);
  return '';
}

function selectOptions(children) {
  const options = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment || child.type === 'optgroup') {
      options.push(...selectOptions(child.props.children));
      return;
    }
    if (child.type !== 'option') return;
    options.push({
      value: String(child.props.value ?? optionText(child.props.children)),
      label: child.props.children,
      text: optionText(child.props.children),
      disabled: Boolean(child.props.disabled),
    });
  });
  return options;
}

/**
 * The one form dropdown used throughout XOmics. It keeps the native select's
 * controlled value/onChange contract, but renders an accessible flat listbox
 * in a portal so dialogs and narrow rails cannot clip it.
 */
export function XomicsSelect({
  value, onChange, children, disabled = false, className = '', id, name,
  'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid, ...rest
}) {
  const generatedId = useId().replace(/:/g, '');
  const controlId = id || `xo-select-${generatedId}`;
  const listId = `${controlId}-listbox`;
  const options = selectOptions(children);
  const normalized = String(value ?? '');
  const selectedIndex = options.findIndex((option) => option.value === normalized);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, selectedIndex));
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const typeahead = useRef({ value: '', at: 0 });

  function updatePosition() {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 10;
    const maxHeight = Math.max(150, Math.min(320, Math.max(spaceBelow, rect.top - 10)));
    const above = spaceBelow < 190 && rect.top > spaceBelow;
    const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 20);
    const left = Math.min(Math.max(10, rect.left), window.innerWidth - width - 10);
    setPosition({
      left, width, maxHeight,
      top: above ? Math.max(10, rect.top - maxHeight - 6) : rect.bottom + 6,
    });
  }

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const reposition = () => updatePosition();
    const closeOutside = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!open || selectedIndex < 0) return;
    setActiveIndex(selectedIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    const menu = menuRef.current;
    if (!open || menu === null) return;
    const active = menu.querySelector('[role="option"].is-active');
    if (active === null) return;
    const bounds = menu.getBoundingClientRect();
    const option = active.getBoundingClientRect();
    if (option.top < bounds.top) menu.scrollTop -= bounds.top - option.top;
    else if (option.bottom > bounds.bottom) menu.scrollTop += option.bottom - bounds.bottom;
  }, [open, activeIndex, position]);

  function nextEnabled(from, direction) {
    if (options.length === 0) return -1;
    for (let step = 1; step <= options.length; step += 1) {
      const candidate = (from + (direction * step) + options.length) % options.length;
      if (!options[candidate].disabled) return candidate;
    }
    return -1;
  }

  function choose(index) {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange?.({
      target: { id: controlId, name, value: option.value },
      currentTarget: { id: controlId, name, value: option.value },
    });
    setOpen(false);
    triggerRef.current?.focus();
  }

  function openMenu(direction = 1) {
    if (disabled || options.length === 0) return;
    const initial = selectedIndex >= 0 && !options[selectedIndex].disabled
      ? selectedIndex : nextEnabled(direction > 0 ? -1 : 0, direction);
    setActiveIndex(Math.max(0, initial));
    setOpen(true);
  }

  function onKeyDown(event) {
    if (event.key === 'Escape' && open) {
      event.preventDefault(); setOpen(false); return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) choose(activeIndex); else openMenu();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      if (!open) openMenu(direction);
      else setActiveIndex(nextEnabled(activeIndex, direction));
      return;
    }
    if (open && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const start = event.key === 'Home' ? -1 : 0;
      setActiveIndex(nextEnabled(start, event.key === 'Home' ? 1 : -1));
      return;
    }
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    const now = Date.now();
    // Keys closer together than a person types come from a barcode scanner.
    // They choose nothing, and what the first of them chose is put back.
    if (now - typeahead.current.at < 40) {
      event.preventDefault();
      const before = typeahead.current.before;
      typeahead.current = { value: '', at: now, before };
      if (!open && before !== undefined && before !== normalized) {
        onChange?.({ target: { id: controlId, name, value: before }, currentTarget: { id: controlId, name, value: before } });
      }
      return;
    }
    const prior = now - typeahead.current.at < 700 ? typeahead.current.value : '';
    const next = `${prior}${event.key}`.toLocaleLowerCase();
    typeahead.current = { value: next, at: now, before: prior === '' ? normalized : typeahead.current.before };
    const match = options.findIndex((option) => !option.disabled
      && option.text.toLocaleLowerCase().startsWith(next));
    if (match >= 0) {
      event.preventDefault();
      if (open) setActiveIndex(match); else choose(match);
    }
  }

  const classes = ['xo-select', className, open ? 'is-open' : '', disabled ? 'is-disabled' : '']
    .filter(Boolean).join(' ');
  return (
    <div className={classes} data-xo-select="" {...rest}>
      {name && <input type="hidden" name={name} value={normalized} />}
      <button ref={triggerRef} id={controlId} type="button" className="xo-select-trigger"
        role="combobox" aria-haspopup="listbox" aria-expanded={open}
        aria-controls={listId} aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
        aria-label={ariaLabel} aria-describedby={ariaDescribedBy} aria-invalid={ariaInvalid}
        disabled={disabled} onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}>
        <span>{selected?.label ?? '—'}</span><ChevronDown aria-hidden="true" />
      </button>
      {open && position !== null && createPortal(
        <div ref={menuRef} id={listId} className="xo-select-menu" role="listbox"
          aria-label={ariaLabel} style={position} onKeyDown={onKeyDown}>
          {options.map((option, index) => (
            <button type="button" role="option" id={`${listId}-${index}`}
              key={`${option.value}-${index}`} aria-selected={index === selectedIndex}
              className={index === activeIndex ? 'is-active' : ''} disabled={option.disabled}
              onMouseEnter={() => setActiveIndex(index)} onClick={() => choose(index)}>
              <span>{option.label}</span>
              {index === selectedIndex && <Check aria-hidden="true" />}
            </button>
          ))}
        </div>,
        // Inside an open modal dialog (a drawer), the list goes into the dialog: everything outside
        // a modal dialog is inert, so a list drawn there could be seen but not clicked.
        triggerRef.current?.closest('dialog[open]') ?? document.getElementById('xo') ?? document.body,
      )}
    </div>
  );
}

// The only header on the page. The shell used to carry a second one — a
// full-width band holding one word and a bell — which meant every screen
// opened with two stacked titles saying the same thing. This is an h1 because
// it is now the document's heading, not a subheading under that band.
// `under` is what stands where the subtitle does when that is more than a line
// of words: whose store a page changes.
export function Head({ title, subtitle, under = null, children }) {
  return (
    <div className="xo-head">
      <div className="xo-head-text">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
        {under}
      </div>
      {children && <div className="xo-head-actions">{children}</div>}
    </div>
  );
}

// What a page is showing, chosen from a list that opens under its name: one
// button that says what is open (`name`), and the page's own list (`children`)
// under it while it is open. `open` and `onOpen` are the page's, so choosing
// from the list closes it. It takes a line where a side column would take a
// third of the page. The list scrolls when it is long; what adds to it
// (`footer`) stays in sight under it.
export function Chooser({
  label, name, Icon = null, open, onOpen, footer = null, children,
}) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointer = (event) => { if (!ref.current?.contains(event.target)) onOpen(false); };
    const closeOnEscape = (event) => { if (event.key === 'Escape') onOpen(false); };
    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onOpen, open]);
  return (
    <div className="xo-chooser" ref={ref}>
      <button type="button" className="xo-chooser-button" aria-haspopup="true" aria-expanded={open}
        aria-label={`${label}: ${name}`} onClick={() => onOpen(!open)}>
        {Icon && <Icon aria-hidden="true" />}<span dir="auto">{name}</span><ChevronDown aria-hidden="true" />
      </button>
      {open && <div className="xo-chooser-list">
        <div className="xo-chooser-items">{children}</div>
        {footer && <div className="xo-chooser-foot">{footer}</div>}
      </div>}
    </div>
  );
}

/** A bordered card: named header, body, and a footer that carries the count. */
export function Card({ title, subtitle, actions, footer, scroll, pad = true, children }) {
  return (
    <section className="xo-card">
      {(title || actions) && (
        <div className="xo-card-top">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="xo-bar-group">{actions}</div>}
        </div>
      )}
      <div className={scroll ? 'xo-card-scroll' : (pad ? 'xo-card-in' : undefined)}>{children}</div>
      {footer && <div className="xo-card-bottom">{footer}</div>}
    </section>
  );
}

/* Loading, empty and error, designed rather than left as a bare sentence. */

export const Loading = ({ label = tx('ui.parts.loading') }) => (
  <div className="xo-state"><Loader2 className="xo-spin" aria-hidden="true" /><strong>{label}</strong></div>
);

export const Empty = ({ title, detail, children }) => (
  <div className="xo-state">
    <Inbox aria-hidden="true" /><strong>{title}</strong>
    {detail && <p>{detail}</p>}{children}
  </div>
);

export const Failed = ({ message, onRetry }) => (
  <div className="xo-state t-bad" role="alert">
    <AlertCircle aria-hidden="true" />
    <strong>{tx("ui.parts.that_did_not_load_1380e339")}</strong>
    <p>{message}</p>
    {onRetry && <button type="button" className="xo-link" onClick={onRetry}>{tx("ui.parts.try_again_042c862e")}</button>}
  </div>
);

export const Pill = ({ tone = 'flat', children }) => (
  <span className={`xo-pill t-${tone}`}>{children}</span>
);

export const Alert = ({ tone = 'info', children }) => (
  <p className={`xo-alert t-${tone}`} role={tone === 'bad' ? 'alert' : undefined}>{children}</p>
);

export function Avatar({ name }) {
  const parts = String(name || '?').trim().split(/\s+/);
  const text = `${parts[0]?.[0] || '?'}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`;
  return <span className="xo-avatar" aria-hidden="true">{text.toUpperCase()}</span>;
}

/** Name over the thing you would search for. */
export const Who = ({ name, detail }) => (
  <div className="xo-who">
    <Avatar name={name} />
    <span className="xo-lines"><strong>{name}</strong>{detail && <span>{detail}</span>}</span>
  </div>
);

export function Meter({ label, value, total, tone }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="xo-meter-row">
      <div className="xo-meter-key"><span>{label}</span><strong>{value} / {total}</strong></div>
      <div className={`xo-meter${tone ? ` t-${tone}` : ''}`} role="progressbar"
        aria-valuenow={value} aria-valuemin={0} aria-valuemax={total} aria-label={label}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Every share in one bar, labelled beneath. Reads faster than four separate
 * meters when the parts are of one whole.
 */
export function SplitBar({ parts }) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  const shown = parts.filter((part) => part.value > 0);
  return (
    <div>
      <div className="xo-split-bar">
        {shown.map((part) => (
          <span
            key={part.label}
            title={tx("ui.parts.label_value_468ddeca", { label: part.label, value: part.value })}
            style={{
              width: `${(part.value / (total || 1)) * 100}%`,
              background: `var(--xo-${part.tone}-fill)`,
            }}
          />
        ))}
      </div>
      <div className="xo-split-key">
        {parts.map((part) => (
          <span key={part.label}>
            <i style={{ background: `var(--xo-${part.tone}-fill)` }} />
            {part.label} <strong>{part.value}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

/** A ranked comparison. Horizontal because the labels are words, not dates. */
export function Bars({ rows, tone = 'info' }) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div className="xo-bars">
      {rows.map((row) => (
        <div key={row.label} className="xo-bar-row">
          <span className="xo-lines">
            <strong style={{ fontWeight: 500 }}>{row.label}</strong>
            {row.detail && <span>{row.detail}</span>}
          </span>
          <span className="xo-bar-track">
            <span className="xo-bar-fill"
              style={{ width: `${(row.value / max) * 100}%`, background: `var(--xo-${row.tone || tone}-fill)` }} />
          </span>
          <strong>{row.display ?? row.value}</strong>
        </div>
      ))}
    </div>
  );
}

/**
 * Submissions over time. Bars rather than a line: the data is a count per day,
 * and a line implies a continuous quantity that was sampled.
 */
export function DayChart({ series, height = 132 }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((day) => day.submitted));
  const width = 100;
  const gap = series.length > 40 ? 0.3 : 1.2;
  const slot = width / series.length;
  const barWidth = Math.max(0.6, slot - gap);

  return (
    <svg className="xo-chart" viewBox={`0 0 ${width} ${height / 4}`} preserveAspectRatio="none"
      role="img" aria-label={tx("ui.parts.analyses_submitted_per_day_over_length_days_4eb5ae75", { length: series.length })}>
      {series.map((day, index) => {
        const total = day.submitted || 0;
        const failed = day.failed || 0;
        const full = (total / max) * (height / 4);
        const bad = (failed / max) * (height / 4);
        const x = index * slot + gap / 2;
        return (
          <g key={day.at}>
            <rect x={x} y={height / 4 - full} width={barWidth} height={full}
              fill="var(--xo-info-fill)" opacity="0.85" rx="0.4" />
            {/* Failures stack at the base in red, so a bad day is visible as a
                shape rather than only in a tooltip. */}
            {bad > 0 && (
              <rect x={x} y={height / 4 - bad} width={barWidth} height={bad}
                fill="var(--xo-bad-fill)" rx="0.4" />
            )}
            <title>{tx("ui.parts.value_total_submitted_failed_failed_9975f546", { value: new Date(day.at).toLocaleDateString(), total: total, failed: failed })}</title>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * The shape of a set of work, as one ring.
 *
 * Four counts that sum to a whole read faster as arcs than as four numbers a
 * person has to add up themselves, and the empty ring is a legible state in its
 * own right rather than a row of zeroes.
 */
export function Arc({ parts, total, caption, size = 168 }) {
  const stroke = 13;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const sum = parts.reduce((count, part) => count + part.value, 0);

  let offset = 0;
  const drawn = parts.filter((part) => part.value > 0).map((part) => {
    const length = (part.value / sum) * circumference;
    const segment = { ...part, length, offset };
    offset += length;
    return segment;
  });

  return (
    <div className="xo-arc" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={parts.map((part) => `${part.value} ${part.label}`).join(', ')}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke="var(--xo-line)" strokeWidth={stroke} />
          {drawn.map((part) => (
            <circle
              key={part.label} cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke={`var(--xo-${part.tone}-fill)`} strokeWidth={stroke} strokeLinecap="butt"
              strokeDasharray={`${part.length} ${circumference - part.length}`}
              strokeDashoffset={-part.offset}
            />
          ))}
        </g>
      </svg>
      <div className="xo-arc-mid">
        <strong><Num value={total} /></strong>
        <span>{caption}</span>
      </div>
    </div>
  );
}

/** A place to go, sized to be clicked rather than read past. */
export function Tile({ icon: Icon, label, detail, tone = 'flat', as: Tag = 'div', ...rest }) {
  return (
    <Tag className="xo-tile" {...rest}>
      <span className={`xo-tile-icon t-${tone}`} aria-hidden="true"><Icon /></span>
      <strong>{label}</strong>
      <span>{detail}</span>
    </Tag>
  );
}

export const Tiles = ({ children }) => <div className="xo-tiles">{children}</div>;

/**
 * Things that happened, on a rail.
 *
 * A table of recent runs asks the reader to compare rows; almost nobody wants
 * that here. They want the order things happened in and whether the last one
 * was fine, which is what a rail with a dot per event actually shows.
 */
export function Timeline({ items }) {
  return (
    <ol className="xo-time">
      {items.map((item) => (
        <li key={item.id}>
          <span className={`xo-time-dot t-${item.tone}`} aria-hidden="true" />
          <div className="xo-time-body">
            <div className="xo-time-top">
              <strong>{item.title}</strong>
              <span className="xo-time-when">{item.when}</span>
            </div>
            <span className="xo-time-detail">{item.detail}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Segmented({ value, options, onChange }) {
  return (
    <div className="xo-seg" role="tablist">
      {options.map((option) => (
        <button key={option.value} type="button" role="tab"
          aria-selected={value === option.value}
          className={value === option.value ? 'is-on' : ''}
          onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Milliseconds as something a person reads, not a number they decode. */
export function duration(ms) {
  if (ms === null || ms === undefined) return '—';
  const minutes = ms / 60000;
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 90) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  return hours < 48 ? `${hours.toFixed(1)}h` : `${Math.round(hours / 24)}d`;
}
